import { useState } from 'react';
import { fetchRefunds } from '@/api/refunds';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportControls } from '@/components/ui/ExportControls';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { refundModeLabel } from '@/constants/defaults';
import { lastNDaysRange } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatCompactCurrency, formatCurrency, formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { RefundRecord, RefundSummaryResponse } from '@/types';

const PAGE_SIZE = 20;

/** Display order + colour for the mode summary tiles. */
const MODE_TILES: { key: string; label: string; dot: string }[] = [
  { key: 'ORIGINAL_PAYMENT', label: 'Original payment', dot: 'bg-blue-500' },
  { key: 'STORE_CREDIT', label: 'Store credit', dot: 'bg-violet-500' },
  // { key: 'UPI_TRANSFER', label: 'UPI transfer', dot: 'bg-emerald-500' },
  { key: 'GIFT_CARD', label: 'Gift card', dot: 'bg-amber-500' },
  { key: 'DISCOUNT_CODE', label: 'Discount code', dot: 'bg-ink' },
  { key: 'PARTIALLY_EXCHANGE', label: 'Partially exchanged', dot: 'bg-ink-subtle' },
 // { key: 'EXCHANGE', label: 'Exchanged', dot: 'bg-ink-subtle' },
];

function refundMode(row: RefundRecord): string | undefined {
  return row.refundMode ?? row.mode;
}

function refundAmount(row: RefundRecord): number | undefined {
  return row.totalAmount ?? row.total ?? row.netRefund ?? row.baseAmount;
}

/** External reference varies by mode: Shopify refund / gift card id / discount code. */
function externalRef(row: RefundRecord): string | null {
  return row.shopifyRefundId ?? row.shopifyGiftCardId ?? row.discountCode ?? null;
}

function refundDate(row: RefundRecord): string | number[] | null | undefined {
  return row.completedAt ?? row.initiatedAt ?? row.triggeredAt ?? row.createdAt;
}

function statusTone(status: string | undefined): 'success' | 'danger' | 'pending' {
  if (status === 'COMPLETED') {
    return 'success';
  }
  if (status === 'FAILED') {
    return 'danger';
  }
  return 'pending';
}

export function RefundsScreen() {
  const [page, setPage] = useState(0);
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));
  const { data, errorMessage, isLoading, reload } = useAsyncData<RefundSummaryResponse | null>(
    (signal) => fetchRefunds(page, PAGE_SIZE, range, signal),
    [page, range],
  );

  const refundsPage = data?.refunds;
  const rows = refundsPage?.content ?? [];
  const countByMode = data?.countByMode ?? {};
  const amountByMode = data?.amountByMode ?? {};

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Refunds"
        description="All money and credit movements."
        actions={
          <>
            <DateRangePicker
              value={range}
              onChange={(nextRange) => {
                setRange(nextRange);
                setPage(0);
              }}
            />
            <ExportControls type="refunds" filenamePrefix="refunds" range={range} />
          </>
        }
      />

      {isLoading ? (
        <LoadingState message="Loading refunds…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <div className="flex flex-col gap-4">
          {/* Mode summary tiles */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {MODE_TILES.map((tile) => (
              <Card key={tile.key} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className={cn('h-2 w-2 rounded-full', tile.dot)} />
                  <span className="text-sm text-ink-muted">{tile.label}</span>
                </div>
                <span className="text-2xl font-bold text-ink">
                  {formatCompactCurrency(amountByMode[tile.key] ?? 0)}
                </span>
                <span className="text-xs text-ink-subtle">
                  {formatNumber(countByMode[tile.key] ?? 0)} refunds
                </span>
              </Card>
            ))}
          </div>

          {/* Refund table */}
          <Card padded={false} className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[920px]">
                <div className="grid grid-cols-[1.1fr_1.3fr_1.1fr_1fr_0.9fr_1fr_0.8fr] items-center gap-3 border-b border-line bg-surface-sunken/60 px-5 py-3">
                  <HeaderCell>Refund · Return</HeaderCell>
                  <HeaderCell>Customer · Orders</HeaderCell>
                  <HeaderCell>Mode · Trigger</HeaderCell>
                  <HeaderCell>Created</HeaderCell>
                  <HeaderCell>Amount</HeaderCell>
                  <HeaderCell>Reference</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                </div>

                {rows.length === 0 ? (
                  <p className="px-5 py-12 text-center text-sm text-ink-muted">No refunds recorded.</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {rows.map((row) => {
                      const reference = externalRef(row);
                      const amount = refundAmount(row);
                      return (
                        <li
                          key={row.refundId ?? row.id ?? row.returnId}
                          className="grid grid-cols-[1.1fr_1.3fr_1.1fr_1fr_0.9fr_1fr_0.8fr] items-center gap-3 px-5 py-3.5"
                        >
                          <div className="min-w-0">
                            <div className="font-mono text-sm font-semibold text-ink">
                              {row.refundId ?? row.id ?? '—'}
                            </div>
                            <div className="truncate font-mono text-xs text-ink-subtle">{row.returnId}</div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm text-ink">
                              {row.customerName || row.customerEmail || '—'}
                            </div>
                            {row.shopifyOrderNumber ? (
                              <div className="truncate font-mono text-xs text-ink-subtle">
                                {row.shopifyOrderNumber}
                                {row.exchangeShopifyOrderNumber ? ` → #${row.exchangeShopifyOrderNumber}` : ''}
                              </div>
                            ) : null}
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm text-ink-muted">{refundModeLabel(refundMode(row))}</div>
                            {row.triggeredBy ? (
                              <div className="truncate text-xs text-ink-subtle">{humanize(row.triggeredBy)}</div>
                            ) : null}
                          </div>

                          <span className="truncate text-sm text-ink-muted">{formatDate(refundDate(row)) || '—'}</span>

                          <span className="text-sm font-semibold text-ink">
                            {amount != null ? formatCurrency(amount) : '—'}
                          </span>

                          <span className="truncate font-mono text-xs text-ink-subtle">{reference ?? '—'}</span>

                          <span title={row.errorMessage ?? undefined}>
                            <Badge tone={statusTone(row.status)}>{row.status ? humanize(row.status) : '—'}</Badge>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </Card>

          {refundsPage && refundsPage.totalPages > 0 ? (
            <Pagination
              page={page}
              totalPages={refundsPage.totalPages}
              totalElements={refundsPage.totalElements}
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function humanize(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function HeaderCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('text-xs font-semibold uppercase tracking-wide text-ink-subtle', className)}>
      {children}
    </span>
  );
}
